/**
Author - Sahil Dhingra
Git - https://github.com/sahildhingra-221
**/

import {
  ContentSection,
  DividerLine,
  LogoImage,
  LogoWrapper,
  PageHeader,
  StaticContent,
  StaticWrapper,
  TableResponsive,
  TableStriped
} from "components/StaticPages/Common";

import GlobalStyle from "static/next/css/PartnerGlobalStyle";
import SafeLink from "components/commons/SafeLink";
import Head from "next/head";
import {NewFooter} from "components";
import React from "react";
import {WealthyEnv} from "helpers";
import {colors} from "nxtconstants";
import styled from "styled-components";

const InvestorCharter = () => {
  const downloadItems = [
    {
      id: 1,
      name: "Stock Broker",
      link: "https://drive.google.com/drive/folders/1jx5uAWqoFI4UJHE3olrlwEQMYm45TJgb"
    },
    {
      id: 2,
      name: "Depository Participant",
      link: "https://drive.google.com/drive/folders/1I4sO-2wKn0C1NK2uuHAUWZ_Veeg_DXwh"
    },
    {
      id: 3,
      name: "Research Analyst",
      link: "https://drive.google.com/drive/folders/1DaFsVPbVzOaDj0FzwuwKX1x0kJalXcrl"
    }
  ];


  const MissionContent = [
    "To provide high quality and dependable service through innovation, capacity enhancement and use of technology.",
    "To establish and maintain a relationship of trust and ethics with the investors.",
    "To observe highest standard of compliances and transparency.",
    "To always keep 'protection of investors interest' as goal while providing service.",
    "To ensure confidentiality of information shared by investors unless such information is required to be provided in furtherance of discharging legal obligations or investors have provided specific consent to share such information."
  ];

  const ServicesContent = [
    "Execution of trades on behalf of investors.",
    "Issuance of Contract Notes.",
    "Issuance of intimations regarding margin due payments.",
    "Facilitate execution of early pay-in obligation instructions.",
    "Periodic Settlement of client’s funds.",
    "Issuance of retention statement of funds at the time of settlement.",
    "Risk management systems to mitigate operational and market risk.",
    "Facilitate client profile changes in the system as instructed by the client.",
    "Information sharing with the client w.r.t. relevant Market Infrastructure Institutions (MII) circulars.",
    "Provide a copy of Rights & Obligations document to the client.",
    "Communicating Most Important terms and Conditions (MITC) to the client.",
    "Redressal of Investor’s grievances."
  ];

  const RightsContent = [
    "Ask for and receive information from a firm about the work history and background of the person handling your account, as well as information about the firm itself (including website providing mandatory information).",
    "Receive complete information about the risks, obligations, and costs of any investment before investing.",
    "Receive a copy of all completed account forms and rights & obligation document.",
    "Receive a copy of ‘Most Important Terms & Conditions’ (MITC).",
    "Receive account statements that are accurate and understandable.",
    "Understand the terms and conditions of transactions you undertake",
    "Access your funds in a timely manner and receive information about any restrictions or limitations on access.",
    "Receive complete information about maintenance or service charges, transaction or redemption fees, and penalties in form of tariff sheet.",
    "Discuss your grievances with compliance officer of the firm and receive prompt attention to and fair consideration of your concerns.",
    "Close your zero balance accounts online with minimal documentation.",
    "Get the copies of all policies (including Most Important Terms and Conditions) of the broker related to dealings of your account.",
    "Not be discriminated against in terms of services offered to equivalent clients.",
    "Get only those advertisement materials from the broker which adhere to Code of Advertisement norms in place.",
    "In case of broker defaults, be compensated from the Exchange Investor Protection Fund as per the norms in place.",
    "Trade in derivatives after submission of relevant financial documents to the broker subject to brokers’ adequate due diligence.",
    "Get warnings on the trading systems while placing orders in securities where surveillance measures are in place.",
    "Get access to products and services in a suitable manner even if differently abled.",
    "Get access to educational materials of the MIIs and brokers.",
    "Get access to all the exchanges of a particular segment you wish to deal with unless opted out specifically as per Broker norms.",
    "Deal with one or more stockbrokers of your choice without any compulsion of minimum business.",
    "Have access to the escalation matrix for communication with the broker.",
    "Not be bound by any clause prescribed by the Brokers which are contravening the Regulatory provisions."
  ];

  const TimelinesContent = [
    {
      activity: "KYC entered into KRA System and CKYCR",
      expectedTimeline: "3 working days of account opening"
    },
    {
      activity: "Client Onboarding",
      expectedTimeline: "Immediate, but not later than one week"
    },
    {
      activity: "Order execution",
      expectedTimeline: "Immediate on receipt of order, but not later than the same day"
    },
    {
      activity: "Allocation of Unique Client Code",
      expectedTimeline: "Before trading"
    },
    {
      activity: "Copy of duly completed Client Registration Documents to clients",
      expectedTimeline: "7 days from the date of upload of Unique Client Code to the Exchange by the trading member"
    },
    {
      activity: "Issuance of contract notes",
      expectedTimeline: "24 hours of execution of trades"
    },
    {
      activity: "Collection of upfront margins from client",
      expectedTimeline: "Before initiation of trade"
    },
    {
      activity: "Issuance of intimations regarding other margin due payments",
      expectedTimeline: "At the end of the T day"
    },
    {
      activity: "Settlement of client funds",
      expectedTimeline: "First Friday/Saturday of the month / quarter as per Exchange pre- announced schedule"
    },
    {
      activity: "‘Statement of Accounts’ for Funds, Securities and Commodities",
      expectedTimeline: "Weekly basis (Within four trading days of Securities and Commodities following week)"
    },
    {
      activity: "Issuance of retention statement of funds/commodities",
      expectedTimeline: "5 days from the date of settlement"
    },
    {
      activity: "Issuance of Annual Global Statement",
      expectedTimeline: "30 days from the end of the financial year"
    },
    {
      activity: "Investor grievances redressal",
      expectedTimeline: "21 Days from the receipt of the complaint"
    }
  ];

  const InvestorDoDont = [
    {
      do: "1. Read all documents and conditions being agreed before signing the account opening",
      dont: "1. Do not deal with unregistered stockbroker."
    },
    {
      do: "2. Receive a copy of KYC, copy of account opening documents and Unique Client Code.",
      dont: "2. Do not forget to strike off blanks in your account opening and KYC."
    },
    {
      do: "3. Read the product / operational framework / timelines related to various Trading and Clearing & Settlement processes.",
      dont: "3. Do not submit an incomplete account opening and KYC form."
    },
    {
      do: "4. Receive all information about brokerage, fees and other charges levied.",
      dont: "4. Do not forget to inform any change in information linked to trading account and obtain confirmation of updation in the system."
    },
    {
      do: "5. Register your mobile number and email ID in your trading, demat and bank accounts to get regular alerts on your transactions.",
      dont: "5. Do not transfer funds, for the purposes of trading to anyone other than a stock broker. No payment should be made in name of employee of stock broker."
    },
    {
      do: "6. If executed, receive a copy of Demat Debit and Pledge Instruction (DDPI). However, DDPI is not a mandatory requirement as per SEBI / Stock Exchanges. Before granting DDPI, carefully examine the scope and implications of powers being granted.",
      dont: "6. Do not ignore any emails / SMSs received with regards to trades done, from the Stock Exchange and raise a concern, if discrepancy is observed."
    },
    {
      do: "7. Receive contract notes for trades executed, showing transaction price, brokerage, GST and STT etc. as applicable, separately, within 24 hours of execution of trades.",
      dont: "7. Do not opt for digital contracts, if not familiar with computers."
    },
    {
      do: "8. Receive funds and securities / commodities on time, as prescribed by SEBI or exchange from time to time.",
      dont: "8. Do not share trading password."
    },
    {
      do: "9. Verify details of trades, contract notes and statement of account and approach relevant authority for any discrepancies. Verify trade details on the Exchange websites from the trade verification facility provided by the Exchanges.",
      dont: "9. Do not fall prey to fixed / guaranteed returns schemes."
    },
    {
      do: "10. Receive statement of accounts periodically. If opted for running account settlement, account has to be settled by the stock broker as per the option given by the client (30 or 90days).",
      dont: "10. Do not fall prey to fraudsters sending emails and SMSs luring to trade in stocks / securities promising huge profits."
    },
    {
      do: "11. In case of any grievances, approach stock broker or Stock Exchange or SEBI for getting the same resolved within prescribed timelines.",
      dont: "11. Do not follow herd mentality for investments. Seek expert and professional advice for your investments."
    },
    {
      do: "12. Retain documents for trading activity as it helps in resolving disputes, if they arise."
    }
  ];

  const ExchangeTimeline = [
    {
      activity: "Receipt of Complaint",
      expectedTimeline: "Day of complaint (C Day)."
    },
    {
      activity: "Additional information sought from the investor, if any, and provisionally forwarded to stock broker.",
      expectedTimeline: "C + 7 Working days."
    },
    {
      activity: "Registration of the complaint and forwarding to the stock broker.",
      expectedTimeline: "C+8 Working Days i.e. T day."
    },
    {
      activity: "Amicable Resolution",
      expectedTimeline: "T+15 Working Days."
    },
    {
      activity: "Refer to Grievance Redressal Committee (GRC), in case of no amicable resolution.",
      expectedTimeline: "T+16 Working Days."
    },
    {
      activity: "Complete resolution process post GRC.",
      expectedTimeline: "T + 30 Working Days."
    },
    {
      activity: "In case where the GRC Member requires additional information, GRC order shall be completed within.",
      expectedTimeline: "T + 45 Working Days."
    },
    {
      activity: "Implementation of GRC Order.",
      expectedTimeline: "On receipt of GRC Order, if the order is in favour of the investor, debit the funds of the stock broker. Order for debit is issued immediately or as per the directions given in GRC order."
    },
    {
      activity: "In case the stock broker is aggrieved by the GRC order, will provide intention to avail arbitration",
      expectedTimeline: "Within 7 days from receipt of order"
    },
    {
      activity: "If intention from stock broker is received and the GRC order amount is upto Rs.20 lakhs",
      expectedTimeline: "Investor is eligible for interim relief from Investor Protection Fund (IPF). The interim relief will be 50% of the GRC order amount or Rs.2 lakhs whichever is less. The same shall be provided after obtaining and Undertaking from the investor."
    },
    {
      activity: "Stock Broker shall file for arbitration",
      expectedTimeline: "Within 6 months from the date of GRC recommendation"
    },
    {
      activity: "In case the stock broker does not file for arbitration within 6 months",
      expectedTimeline: "The GRC order amount shall be released to the investor after adjusting the amount released as interim relief, if any."
    }
  ];

  const GrievanceRedressal = [
    {
      activity: "Investor complaint/Grievances",
      process: "Investor can lodge complaint/grievance against stock broker in the following ways: ",
      heading: "Mode of filing the complaint with stock broker",
      description: "Investor can approach the Stock Broker at the designated Investor Grievance e-mail ID of the stock broker. The Stock Broker will strive to redress the grievance immediately, but not later than 21 days of the receipt of the grievance.",
      head2: "Mode of filing the complaint with stock exchanges",
      description2: (
        <>
          1. SCORES 2.0 (a web based centralized grievance redressal system of SEBI){" "}
          <SafeLink href="https://scores.sebi.gov.in" target="_blank" rel="noopener noreferrer">
            https://scores.sebi.gov.in
          </SafeLink>

        </>
      ),
      subhead: "Two level review for complaint/grievance against stock broker:",
      bullet: "First review done by Designated body/Exchange",
      bullet1: "Second Review done by SEBI",
      description02: "2. Emails to designated email IDs of Exchange"

    },

    {
      activity: "Online Dispute Resolution (ODR) platform for online Conciliation and Arbitration.",
      process: "If the Investor is not satisfied with the resolution provided by the Market Participants, then the Investor has the option to file the complaint/ grievance on SMARTODR platform for its resolution through online conciliation or arbitration."
    },
    {
      activity: "Steps to be followed in ODR for Review, Conciliation and Arbitration.",
      process: "1. Investor to approach Market Participant for redressal of complaint",
      description: "2. If investor is not satisfied with response of Market Participant, he/she has either of the following 2 options:",
      bullet: "May escalate the complaint on SEBI SCORES portal.",
      bullet1: "May also file a complaint on SMARTODR portal for its resolution through online conciliation and arbitration.",
      description3: "Upon receipt of complaint on SMARTODR portal, the relevant MII will  review the matter and endeavor to resolve the matter between the Market  Participant and investor within 21 days.",
      description4: "3. Upon receipt of complaint on SMARTODR portal, the relevant MII will review the matter and endeavor to resolve the matter between the Market Participant and investor within 21 days.",
      description5: "4. If the matter could not be amicably resolved, then the matter shall be referred for conciliation.",
      description6: "5. During the conciliation process, the conciliator will endeavor for amicable settlement of the dispute within 21 days, which may be extended with 10 days by the conciliator with consent of the parties to dispute.",
      description7: "6. If the conciliation is unsuccessful, then the investor may request to refer the matter for arbitration.",
      description8: "7. The arbitration process to be concluded by arbitrator(s) within 30 days, which is extendable by 30 days with consent of the parties to dispute."
    }


  ];

  const ExchangeInvestorContent = [
    "Circular is issued to inform about declaration of Stock Broker as Defaulter.",
    "Information of defaulter stock broker is disseminated on Stock Exchange website.",
    "Public Notice is issued informing declaration of a stock broker as defaulter and inviting claims within specified period",
    "Intimation to clients of defaulter stock brokers via emails and SMS for facilitating lodging of claims within the specified period."
  ];

  const ExchangeInfoContent = [
    "Norms for eligibility of claims for compensation from IPF",
    "Claim form for lodging claim against defaulter stock broker.",
    "FAQ on processing of investors’ claims against Defaulter stock broker.",
    "Provision to check online status of client’s claim.",
    "Standard Operating Procedure (SOP) for handling of Claims of Investors in the Cases of Default by Brokers.",
    "Claim processing policy against Defaulter/Expelled members.",
    "List of Defaulter/Expelled members and public notice issued."
  ];

  const ParticipantsMissionContent = [
    "To hold securities of investors in dematerialized form and facilitate its transfer, while ensuring safekeeping of securities and protecting interest of investors.",
    "To provide timely and accurate information to investors with regard to their holding and transfer of securities held by them.",
    "To provide the highest standards of investor education, investor awareness and timely services so as to enhance Investor Protection and create awareness about Investor Rights."
  ];

  const BasicServicesContent = [
    {
      activity: "Dematerialization of securities",
      expectedTimeline: "7 days"
    },
    {
      activity: "Rematerialization of securities",
      expectedTimeline: "7 days"
    },
    {
      activity: "Mutual Fund Conversion / Destatementization",
      expectedTimeline: "5 days"
    },
    {
      activity: "Re-conversion / Restatementisation of Mutual fund units",
      expectedTimeline: "7 days"
    },
    {
      activity: "Transmission of securities",
      expectedTimeline: "7 days"
    },
    {
      activity: "Registering pledge request",
      expectedTimeline: "15 days"
    },
    {
      activity: "Closure of demat account",
      expectedTimeline: "30 days"
    },
    {
      activity: "Settlement Instruction",
      expectedTimeline: (
        <>
          For T+1 day settlements, Participants shall accept instructions from the Clients, in physical form up to 4 p.m. (in case of electronic instructions up to 6.00 p.m.) on T day for pay-in of securities.
          <br />  For T+0 day settlements, Participants shall accept EPI instructions from the clients, till 11:00 AM on T day.
          <br /> Note: ‘T’ refers ‘Trade Day’
        </>
      )
    }
  ];

  const ServicesBriefContent = [
    {
      activity: "Value Added Services",
      brief: [
        {
          detail: "Depositories also provide value added services such as Basic Services Demat Account (BSDA) - ",
          url: "#ValueAddedServices",
          urlExternal: "https://www.cdslindia.com/Investors/Investorcharter.html"
        },
        {
          detail: "TPIN facility to authorise debits - ",
          url: "#ValueAddedServices",
          urlExternal: "https://www.cdslindia.com/Investors/InvestorCharter.html"
        },
        {
          detail: "Linkages with Clearing System - ",
          url: "#ValueAddedServices",
          urlExternal: "https://www.cdslindia.com/Investors/Investorcharter. html"
        },
        {
          detail: "Transposition cum dematerialization - ",
          url: "#ValueAddedServices",
          urlExternal: "https://www.cdslindia.com/Investors/Investorcha rter.html"
        },
        {
          detail: "Distribution of cash and non-cash corporate benefits (Bonus, Rights, IPOs etc.), stock lending, demat of NSC / KVP, demat of warehouse receipts etc."
        }
      ]
    },
    {
      activity: "Consolidated Account statement (CAS)",
      brief: [
        {
          detail: "CAS is issued 10 days from the end of the month (if there were transactions in the previous month) or half yearly (if no transactions)."
        }
      ]
    },
    {
      activity: "Digitalization of provided services by the depositories",
      brief: [
        {
          detail: "Depositories offer below technology solutions and e-facilities to their demat account holders through DPs:"
        },
        {
          detail: (
            <>
              <b> a. E - Account Opening:  </b>
            </>
          ),
          url: "#Digitization",
          urlExternal: "https://www.wealthy.in/account-opening-process"
        },
        {
          detail: (
            <>
              <b> b. Online instructions for execution:  </b>
            </>
          ),
          url: "#Digitization",
          urlExternal: "https://web.cdslindia.com/myeasitoken/home/login"
        },
        {
          detail: (
            <>
              <b> c. e-DIS / Demat Gateway:   </b>
            </>
          ),
          url: "#Digitization",
          urlExternal: "https://edis.cdslindia.com/home/changepin"
        },
        {
          detail: (
            <>
              <b> d. e-CAS facility:  </b>
            </>
          ),
          url: "#Digitization",
          urlExternal: "https://www.cdslindia.com/CAS/LoginCAS.aspx"
        },
        {
          detail: (
            <>
              <b> e. Miscellaneous services: link <br />
                Details available on the link provided below :  </b>
            </>
          ),
          url: "#Digitization",
          urlExternal: "https://www.cdslindia.com/Investors/Investorcharter.html"
        }
      ]
    }
  ];

  const GrievanceProcessContent = [
    {
      activity: "Investor Complaint/ Grievances",
      brief: [
        {
          detail: "Investor can lodge complaint/ grievance against the Depository/DP in the following ways:"
        },
        {
          detail: "a) electronic mode - SCORES 2.0 (a web based centralized grievance redressal system of SEBI)",
          urlExternal: "https://scores.sebi.gov.in/"
        },
        {
          detail: (
            <>
              Two Level Review for complaint/grievance against DP:<br /> - First review done by Designated Body <br />- Second review done by SEBI"
            </>
          )
        },
        {
          detail: "Respective Depository's web portal dedicated for the filing of compliant",
          urlExternal: "https://www.cdslindia.com/Footer/grievances.aspx"
        },
        {
          detail: "Emails to designated email IDs of Depository  'complaints@cdslindia.com'"
        },
        {
          detail: "b) Offline mode "
        },
        {
          detail: (
            <>
              <b> Investors can send physical letters to CDSL-on-CDSL registered office address. Marathon Futurex, A-Wing, 25th floor, NM Joshi Marg, Lower Parel, Mumbai 400013 <br /> The complaints/ grievances lodged directly with the Depository shall be resolved within 21 days. </b>
            </>
          )
        }
      ]
    },
    {
      activity: "Online Dispute Resolution (ODR) platform for online Conciliation and Arbitration",
      brief: [
        {
          detail: "If the Investor is not satisfied with the resolution provided by DP or other Market Participants, then the Investor has the option to file the complaint/ grievance on SMARTODR platform for its resolution through by online conciliation or arbitration. ",
          url: "#InvestorCharter",
          urlExternal: "https://smartodr.in/login"
        }
      ]
    },
    {
      activity: "Arbitration proceedings",
      brief: [
        {
          detail: (
            <>
              <ul>
                <li>
                  Investor to approach Market Participant for redressal of complaint
                </li>
                <li>
                  If investor is not satisfied with response of Market Participant, he/she can escalate the complaint on SEBI SCORES portal.
                </li>
                <li>
                  Alternatively, the investor may also file a complaint on SMARTODR portal for its resolution through online conciliation and arbitration.
                </li>
                <li>
                  Upon receipt of complaint on SMARTODR portal, the relevant MII will review the matter and endeavour to resolve the matter between the Market Participant and investor within 21 days.
                </li>
                <li>
                  If the matter could not be amicably resolved, then the Investor may request the MII to refer the matter case for conciliation.
                </li>
                <li>
                  During the conciliation process, the conciliator will endeavor for amicable settlement of the dispute within 21 days, which may be extended with 10 days by the conciliator.
                </li>
                <li>
                  If the conciliation is unsuccessful, then the investor may request to refer the matter for arbitration.
                </li>
              </ul>
              The arbitration process to be concluded by arbitrator(s) within 30 days, which is extendable by 30 days
            </>
          )

        }
      ]
    }
  ];

  const InvestorGuidance = [
    "Always deal with a SEBI registered Depository Participant for opening a demat account.",
    "Read all the documents carefully before signing them.",
    "Before granting Power of attorney to operate your demat account to an intermediary like Stockbroker, Portfolio Management Services (PMS) etc., carefully examine the scope and implications of powers being granted.",
    "Always make payments to registered intermediary using banking channels. No payment should be made in name of employee of intermediary.",
    "Accept the Delivery Instruction Slip (DIS) book from your DP only (pre-printed with a serial number along with your Client ID) and keep it in safe custody and do not sign or issue blank or partially filled DIS slips",
    "Always mention the details like ISIN, number of securities accurately. In case of any queries, please contact your DP or broker and it should be signed by all demat account holders.",
    "Strike out any blank space on the slip and Cancellations or corrections on the DIS should be initialed or signed by all the account holder(s).",
    "Do not leave your instruction slip book with anyone else.",
    "Do not sign blank DIS as it is equivalent to a bearer cheque.",
    "Inform any change in your Personal Information (for example address or Bank Account details, email ID, Mobile number) linked to your demat account in the prescribed format and obtain confirmation of updation in system.",
    "Mention your Mobile Number and email ID in account opening form to receive SMS alerts and regular updates directly from depository.",
    "Always ensure that the mobile number and email ID linked to your demat account are the same as provided at the time of account opening/updation.",
    "Do not share password of your online trading and demat account with anyone.",
    "Do not share One Time Password (OTP) received from banks, brokers, etc. These are meant to be used by you only",
    "Do not share login credentials of e-facilities provided by the depositories such as e-DIS/demat gateway, SPEED-e/easiest etc. with anyone else.",
    "Demat is mandatory for any transfer of securities of Listed public limited companies with few exceptions.",
    "Keep a record of documents signed, DIS issued and account statements received.",
    "As Investors you are required to verify the transaction statement carefully for all debits and credits in your account. In case of any unauthorized debit or credit, inform the DP or your respective Depository.",
    "Appoint a nominee to facilitate your heirs in obtaining the securities in your demat account, on completion of the necessary procedures.",
    "Register for Depository's internet-based facility or download mobile app of the depository to monitor your holdings.",
    "Ensure that, both, your holding and transaction statements are received periodically as instructed to your DP. You are entitled to receive a transaction statement every month if you have any transactions.",
    "Do not follow herd mentality for investments. Seek expert and professional advice for your investments.",
    "Beware of assured/fixed returns."
  ];

  const InvestorRights = [
    "Receive a copy of KYC, copy of account opening documents.",
    "No minimum balance is required to be maintained in a demat account.",
    "No charges are payable for opening of demat accounts.",
    "If executed, receive a copy of Power of Attorney. However, Power of Attorney is not a mandatory requirement as per SEBI / Stock Exchanges. You have the right to revoke any authorization given at any time.",
    "You can open more than one demat account in the same name with single DP/ multiple DPs.",
    "Receive statement of accounts periodically. In case of any discrepancies in statements, take up the same with the DP immediately. If the DP does not respond, take up the matter with the Depositories.",
    "Pledge and /or any other interest or encumbrance can be created on demat holdings.",
    "Right to give standing instructions with regard to the crediting of securities in demat account.",
    "Investor can exercise its right to freeze/defreeze his/her demat account or specific securities / specific quantity of securities in the account, maintained with the DP.",
    "In case of any grievances, Investor has right to approach Participant or Depository or SEBI for getting the same resolved within prescribed timelines.",
    "Every eligible investor shareholder has a right to cast its vote on various resolutions proposed by the companies for which Depositories have developed an internet based 'e-Voting' platform.",
    "Receive information about charges and fees. Any charges/tariff agreed upon shall not increase unless a notice in writing of not less than thirty days is given to the Investor.",
    "Right to indemnification for any loss caused due to the negligence of the Depository or the participant.",
    "Right to opt out of the Depository system in respect of any security."
  ];

  const ArbitrationProceedings = [
    "Investor to approach Market Participant for redressal of complaint.",
    "If investor is not satisfied with response of Market Participant, he/she can escalate the complaint on SEBI SCORES portal.",
    "Alternatively, the investor may also file a complaint on SMARTODR portal for its resolution through online conciliation and arbitration.",
    "Upon receipt of complaint on SMARTODR portal, the relevant MII will review the matter and endeavour to resolve the matter between the Market Participant and investor within 21 days.",
    "If the matter could not be amicably resolved, then the Investor may request the MII to refer the matter case for conciliation.",
    "During the conciliation process, the conciliator will endeavor for amicable settlement of the dispute within 21 days, which may be extended with 10 days by the conciliator.",
    "If the conciliation is unsuccessful, then the investor may request to refer the matter for arbitration."
  ];

  const InvestorResponsibilites = [
    "Deal with a SEBI registered DP for opening demat account, KYC and Depository activities.",
    "Provide complete documents for account opening and KYC (Know Your Client). Fill all the required details in Account Opening Form / KYC form in own handwriting and cancel out the blanks",
    "Read all documents and conditions being agreed before signing the account opening form.",
    "Accept the Delivery Instruction Slip (DIS) book from DP only (preprinted with a serial number along with client ID) and keep it in safe custody and do not sign or issue blank or partially filled DIS.",
    "Always mention the details like ISIN, number of securities accurately.",
    "Inform any change in information linked to demat account and obtain confirmation of updation in the system.",
    "Regularly verify balances and demat statement and reconcile with trades / transactions.",
    "Appoint nominee(s) to facilitate heirs in obtaining the securities in their demat account.",
    "Do not fall prey to fraudsters sending emails and SMSs luring to trade in stocks / securities promising huge profits."
  ];

  const ConductforDepositor = [
    "Always abide by the provisions of the Act, Depositories Act, 1996, any Rules or Regulations framed thereunder, circulars, guidelines and any other directions issued by the Board from time to time.",
    "Adopt appropriate due diligence measures.",
    "Take effective measures to ensure implementation of proper risk management framework and good governance practices.",
    "Take appropriate measures towards investor protection and education of investors.",
    "Treat all its applicants/members in a fair and transparent manner.",
    "Promptly inform the Board of violations of the provisions of the Act, the Depositories Act, 1996, rules, regulations, circulars, guidelines or any other directions by any of its issuer or issuer’s agent.",
    "Take a proactive and responsible attitude towards safeguarding the interests of investors, integrity of depository’s systems and the securities market.",
    "Endeavor for introduction of best business practices amongst itself and its members.",
    "Act in utmost good faith and shall avoid conflict of interest in the conduct of its functions.",
    "Not indulge in unfair competition, which is likely to harm the interests of any other Depository, their participants or investors or is likely to place them in a disadvantageous position while competing for or executing any assignment.",
    <p>
      Segregate roles and responsibilities of key management personnel within the depository including <br />
      1. Clearly mapping legal and regulatory duties to the concerned position. <br />
      2. Defining delegation of powers to each position. <br />
      3. Assigning regulatory, risk management and compliance aspects to business and support teams.
      <ul>
        <li>
          Be responsible for the acts or omissions of its employees in respect of the conduct of its business.
        </li>
        <li>
          Monitor the compliance of the rules and regulations by the participants and shall further ensure that their conduct is in a manner that will safeguard the interest of investors and the securities market.
        </li>
      </ul>
    </p>

  ];

  const ConductforParticipants = [
    "A participant shall make all efforts to protect the interests of investors.",
    <p>
      A participant shall always endeavour to— <br />
      1. render the best possible advice to the clients having regard to the client’s needs and the environments and his own professional skills; <br />
      2. ensure that all professional dealings are effected in a prompt, effective and efficient manner; <br />
      3. inquiries from investors are adequately dealt with; <br />
      4. grievances of investors are redressed without any delay.
    </p>,
    "A participant shall maintain high standards of integrity in all its dealings with its clients and other intermediaries, in the conduct of its business.",
    "A participant shall be prompt and diligent in opening of a beneficial owner account, dispatch of the dematerialisation request form, rematerialisation request form and execution of debit instruction slip and in all the other activities undertaken by him on behalf of the beneficial owners.",
    "A participant shall endeavour to resolve all the complaints against it or in respect of the activities carried out by it as quickly as possible, and not later than one month of receipt.",
    "A participant shall not increase charges/fees for the services rendered without proper advance notice to the beneficial owners.",
    "A participant shall not indulge in any unfair competition, which is likely to harm the interests of other participants or investors or is likely to place such other participants in a disadvantageous position while competing for or executing any assignment.",
    "A participant shall not make any exaggerated statement whether oral or written to the clients either about its qualifications or capability to render certain services or about its achievements in regard to services rendered to other clients.",
    "A participant shall not divulge to other clients, press or any other person any information about its clients which has come to its knowledge except with the approval/authorisation of the clients or when it is required to disclose the information under the requirements of any Act, Rules or Regulations.",
    "A participant shall co-operate with the Board as and when required.",
    "A participant shall maintain the required level of knowledge and competency and abide by the provisions of the Act, Rules, Regulations and circulars and directions issued by the Board. The participant shall also comply with the award of the Ombudsman passed under the Securities and Exchange Board of India (Ombudsman) Regulations, 2003.",
    "A participant shall not make any untrue statement or suppress any material fact in any documents, reports, papers or information furnished to the Board.",
    "A participant shall not neglect or fail or refuse to submit to the Board or other agencies with which it is registered, such books, documents, correspondence, and papers or any part thereof as may be demanded/requested from time to time.",
    "A participant shall ensure that the Board is promptly informed about any action, legal proceedings, etc., initiated against it in respect of material breach or non- compliance by it, of any law, Rules, regulations, directions of the Board or of any other regulatory body.",
    "A participant shall maintain proper inward system for all types of mail received in all forms.",
    "A participant shall follow the maker—Checker concept in all of its activities to ensure the accuracy of the data and as a mechanism to check unauthorised transaction.",
    "A participant shall take adequate and necessary steps to ensure that continuity in data and record keeping is maintained and that the data or records are not lost or destroyed. It shall also ensure that for electronic records and data, up- to-date back up is always available with it.",
    "A participant shall provide adequate freedom and powers to its compliance officer for the effective discharge of his duties.",
    "A participant shall ensure that it has satisfactory internal control procedures in place as well as adequate financial and operational capabilities which can be reasonably expected to take care of any losses arising due to theft, fraud and other dishonest acts, professional misconduct or omissions.",
    "A participant shall be responsible for the acts or omissions of its employees and agents in respect of the conduct of its business.",
    "A participant shall ensure that the senior management, particularly decision makers have access to all relevant information about the business on a timely basis.",
    "A participant shall ensure that good corporate policies and corporate governance are in place."

  ];

  const ComplianceContent = [
    "An Investor / client can make a written complaint through letter also.",
    "The Company maintains investor grievance register in which full detail of every written complaint shall entered. ",
    "Designated person shall login the designated investor grievances email id on daily basis to look after the investor complaint whether new complaint has been lodged or not.",
    "The full detail of the written complaint must be passed to the concerned department and inform the compliance officer of the company as soon as it is received.",
    "A letter or mail must be written to all the investor who have submitted written complaints by the designated person or Compliance Officer acknowledging receipt of the complaint and informing them it will be dealt with.",
    "Compliance Department will obtain all information available on the complaint which is considered necessary for a proper investigation. Look into all the necessary information and resolve the as soon as possible.",
    "There is standing policy of the company to resolve the investor complaint within seven days of the receipt of the same expect the complicated case.",
    "A serious complaint (where the written response does not settle the issue) must be referred to the Designated Director of the company.",
    "The Compliance Officer of the Company shall review the investor complaint register on weekly basis to find out whether complaint has been resolved within time or not."
  ];

  const StockExchanges = [
    {
      exchange: "BSE",
      urlText: "Click Here (For BSE transaction)",
      actualUrl: "https://bsecrs.bseindia.com/ecomplaint/frmInvestorHome.aspx",
      tel: "022-22728517",
      email: "dis@bseindia.com"
    },
    {
      exchange: "NSE",
      urlText: "Click Here  (For NSE transaction)",
      actualUrl: "https://investorhelpline.nseindia.com/NICEPLUS/",
      tel: "1800-266-0058",
      email: "ignse@nseindia.co.in"
    },
    {
      exchange: "DP - CDSL",
      urlText: "Click Here  (For DP transaction)",
      actualUrl: "https://www.cdslindia.com/Footer/grievances.aspx",
      tel: "1800-22-5533",
      email: "complaints@cdslindia.com"
    }
  ];

  return (
    <>
      <Head>
        <title>Investor Charter | Wealthy</title>
        <meta
          content="Read Wealthy's Investor Charter to understand your rights, responsibilities, and the service standards you can expect as a valued investor on our platform."
          name="description"
        />
        <link href={`${WealthyEnv.URL}/investor-charter`} rel="canonical" />
        <link href="https://i.wlycdn.com/wealthy-home-page/new-fav-icon.png" rel="icon" />
      </Head>
      <GlobalStyle />
      <LogoWrapper>
        <SafeLink href={WealthyEnv.URL}>
          <LogoImage
            alt="Wealthy"
            src={`${WealthyEnv.HOMEPAGE_CDN_URL}/wealthy-new-purple-logo.svg`}
          />
        </SafeLink>
      </LogoWrapper>
      <StaticWrapper>
        <DividerLine />
        <PageHeader>
          Investor Charter
        </PageHeader>
        <DividerLine />
        <StaticContent>

          <ContentSection>
            <h2>Vision</h2>
            <p>
              To follow highest standards of ethics and compliances while facilitating the trading by clients in securities in a fair and transparent manner, so as to contribute in creation of wealth for investors.
            </p>
          </ContentSection>

          <ContentSection>
            <h2>Mission</h2>
            <ul>
              {MissionContent.map((MissionDataItem) => {
                return (
                  <>
                    <li>
                      <p>
                        {MissionDataItem}
                      </p>
                    </li>
                  </>
                );
              })}
            </ul>
          </ContentSection>

          <ContentSection>
            <h2>Services provided to Investors</h2>
            <ul>
              {ServicesContent.map((ServicesDataItem) => {
                return (
                  <>
                    <li>
                      <p>
                        {ServicesDataItem}
                      </p>
                    </li>
                  </>
                );
              })}
            </ul>
          </ContentSection>

          <ContentSection>
            <h2>Rights of Investors</h2>
            <ul>
              {RightsContent.map((RightsDataItem) => {
                return (
                  <>
                    <li>
                      <p>
                        {RightsDataItem}
                      </p>
                    </li>
                  </>
                );
              })}
            </ul>
          </ContentSection>

          <ContentSection>
            <h2>Various activities of Stock Brokers with timelines</h2>
            <TableResponsive>
              <TableStriped>
                <thead>
                  <tr>
                    <th>
                      S.No.
                    </th>
                    <th>
                      Activities
                    </th>
                    <th>
                      Expected Timelines
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {TimelinesContent.map((TimelinesContentItem, index) => {
                    return (
                      <>
                        <tr>
                          <td>
                            {index + 1}
                          </td>
                          <td>
                            {TimelinesContentItem.activity}
                          </td>
                          <td>
                            {TimelinesContentItem.expectedTimeline}
                          </td>
                        </tr>
                      </>
                    );
                  })}
                </tbody>
              </TableStriped>
            </TableResponsive>
          </ContentSection>

          <ContentSection>
            <h2>DOs and DON’Ts for Investors</h2>
            <TableResponsive>
              <TableStriped>
                <thead>
                  <tr>
                    <th>
                      DO's
                    </th>
                    <th>
                      DON'Ts
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {InvestorDoDont.map((InvestorDoDontItem) => {
                    return (
                      <>
                        <tr>
                          <td>
                            {InvestorDoDontItem.do}
                          </td>
                          <td>
                            {InvestorDoDontItem.dont}
                          </td>
                        </tr>
                      </>
                    );
                  })}

                </tbody>
              </TableStriped>
            </TableResponsive>
            <p style={{textAlign: "center"}}>
              Additionally, Investors may refer to Dos and Don’ts issued by MIIs on their respective websites from time to time.
            </p>
          </ContentSection>

          <ContentSection>
            <h2>Grievance Redressal Mechanism</h2>
            <p>
              <span>Level 1 –</span> Approach the Stock Broker at the designated Investor Grievance e-mail ID of the stock broker. The Stock Broker will strive to redress the grievance immediately, but not later than 30 days of the receipt of the grievance.
            </p>
            <p>
              <span>Level 2 –</span> Approach the Stock Exchange using the grievance mechanism mentioned at the website of the respective exchange.
            </p>
            <p>
              Complaints Resolution Process at Stock Exchange explained graphically
            </p>

            <img
              alt=""
              loading="lazy"
              src={"https://i.wlycdn.com/broking-compliance/complaint-lodge.png"}
            />

          </ContentSection>

          <ContentSection>
            <h2>The process of investor grievance redressal is as follows:</h2>
            <TableResponsive>
              <TableStriped>
                <thead>
                  <tr />
                </thead>
                <tbody>
                  {GrievanceRedressal.map((TimelinesItem, index) => {
                    return (
                      <>
                        <tr>
                          <td>
                            {index + 1}
                          </td>
                          <td>
                            {TimelinesItem.activity}
                          </td>
                          <td>
                            {TimelinesItem.process} <br />
                            <p style={{textDecoration: "underline", fontWeight: 900}}>
                              {TimelinesItem.heading}
                            </p>
                            <p >
                              {TimelinesItem.description}
                            </p>
                            <p style={{textDecoration: "underline", fontWeight: 900}}>
                              {TimelinesItem.head2}
                            </p>
                            <p style={{paddingLeft: "20px"}}>
                              {TimelinesItem.description2}
                            </p>
                            <p style={{textDecoration: "underline"}}>
                              {TimelinesItem.subhead}
                            </p>
                            {index !== 1 && (
                              <>
                                <li style={{paddingLeft: "20px"}}>{TimelinesItem.bullet}</li>
                                <li style={{paddingLeft: "20px"}}>{TimelinesItem.bullet1}</li>
                              </>
                            )}
                            <p style={{paddingLeft: "20px"}}>
                              {TimelinesItem.description02}
                            </p>
                            <p>
                              {TimelinesItem.description4}
                            </p>
                            <p>
                              {TimelinesItem.description5}
                            </p>
                            <p>
                              {TimelinesItem.description6}
                            </p>
                            <p>
                              {TimelinesItem.description7}
                            </p>
                            <p>
                              {TimelinesItem.description8}
                            </p>

                          </td>
                        </tr>
                      </>
                    );
                  })}
                </tbody>
              </TableStriped>
            </TableResponsive>
          </ContentSection>

          <ContentSection>
            <h2>Timelines for complaint resolution process at Stock Exchange against brokers</h2>
            <TableResponsive>
              <TableStriped>
                <thead>
                  <tr>
                    <th>
                      S.No.
                    </th>
                    <th>
                      Activities
                    </th>
                    <th>
                      Expected Timelines
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ExchangeTimeline.map((ExchangeTimelineItem, index) => {
                    return (
                      <>
                        <tr>
                          <td>
                            {index + 1}
                          </td>
                          <td>
                            {ExchangeTimelineItem.activity}
                          </td>
                          <td>
                            {ExchangeTimelineItem.expectedTimeline}
                          </td>
                        </tr>
                      </>
                    );
                  })}
                </tbody>
              </TableStriped>
            </TableResponsive>
          </ContentSection>

          <ContentSection>
            <h2>Handling of Investor’s claims / complaints in case of default of a Trading Member / Clearing Member (TM/CM)</h2>
            <h3>Default of TM/CM</h3>
            <p>Following steps are carried out by Stock Exchange for benefit of investor, in case stockbroker defaults</p>
            <ul>
              {ExchangeInvestorContent.map((ExchangeInvestorDataItem) => {
                return (
                  <>
                    <li>
                      <p>
                        {ExchangeInvestorDataItem}
                      </p>
                    </li>
                  </>
                );
              })}
            </ul>
          </ContentSection>

          <ContentSection>
            <h2>Following information is available on Stock Exchange website for information of investors</h2>
            <ul>
              {ExchangeInfoContent.map((ExchangeInfoDataItem) => {
                return (
                  <>
                    <li>
                      <p>
                        {ExchangeInfoDataItem}
                      </p>
                    </li>
                  </>
                );
              })}
            </ul>
            <p>
              <span>Level 3 –</span>he complaint not redressed at Stock Broker / Stock Exchange level, may be lodged with SEBI on SCORES (a web based centralized grievance redressal system of SEBI) @
              <SafeLink
                href="https://scores.sebi.gov.in/"
                rel="noopener noreferrer"
                target="_blank"
              > https://scores.sebi.gov.in/</SafeLink>
            </p>
          </ContentSection>

          <ContentSection>
            <h2>INVESTOR CHARTER FOR DEPOSITORIES AND DEPOSITORY PARTICIPANTS</h2>
            <h2>Vision</h2>
            <p>
              Towards making Indian Securities Market - Transparent, Efficient, & Investor friendly by providing safe, reliable, transparent and trusted record keeping platform for investors to hold and transfer securities in dematerialized form.
            </p>
          </ContentSection>

          <ContentSection>
            <h2>Mission</h2>
            <ul>
              {ParticipantsMissionContent.map((ParticipantsMissionDataItem) => {
                return (
                  <>
                    <li>
                      <p>
                        {ParticipantsMissionDataItem}
                      </p>
                    </li>
                  </>
                );
              })}
            </ul>
          </ContentSection>

          <ContentSection>
            <h2>Details of Business transacted by the Depository and Depository Participant (DP)</h2>
            <p>
              A Depository is an organization which holds securities of investors in electronic form. Depositories provide services to various market participants – Exchanges, Clearing Corporations, Depository Participants (DPs), Issuers and Investors in both primary as well as secondary markets. The depository carries out its activities through its agents which are known
              as Depository Participants (DP). Details available on the link.
              <br /> <SafeLink href="https://www.cdslindia.com/DP/dplist.aspx" target="_blank" rel="noopener noreferrer">
                https://www.cdslindia.com/DP/dplist.aspx
              </SafeLink>

            </p>
          </ContentSection>

          <ContentSection>
            <h2>Description of services provided by the Depository through Depository Participants (DPs) to investors</h2>
            <h2>1. Basic Services</h2>
            <TableResponsive>
              <TableStriped>
                <thead>
                  <tr>
                    <th>
                      S.No.
                    </th>
                    <th>
                      Brief about the Activity / Service
                    </th>
                    <th>
                      Expected Timelines for processing by the DP after receipt of proper documents
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {BasicServicesContent.map((BasicServicesItem, index) => {
                    return (
                      <>
                        <tr>
                          <td>
                            {index + 1}
                          </td>
                          <td>
                            {BasicServicesItem.activity}
                          </td>
                          <td>
                            {BasicServicesItem.expectedTimeline}
                          </td>
                        </tr>
                      </>
                    );
                  })}
                </tbody>
              </TableStriped>
            </TableResponsive>
          </ContentSection>

          <ContentSection>
            <h2>2. Depositories provide special services like pledge, hypothecation, internet-based services etc. in addition to their core services and these include.</h2>
            <TableResponsive>
              <TableStriped>
                <thead>
                  <tr>
                    <th>
                      S.No.
                    </th>
                    <th>
                      Type of Activity /Service
                    </th>
                    <th>
                      Brief about the Activity / Service
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ServicesBriefContent.map((ServicesBriefItem, index) => {
                    return (
                      <>
                        <tr>
                          <td>
                            {index + 1}
                          </td>
                          <td>
                            {ServicesBriefItem.activity}
                          </td>
                          <td>
                            {ServicesBriefItem.brief.map((briefItem) => {
                              return (
                                <>
                                  <p>
                                    {briefItem.detail}
                                    {briefItem.url &&
                                      (
                                        <SafeLink href={briefItem.url}>Link</SafeLink>
                                      )
                                    }
                                    {briefItem.urlExternal &&
                                      (
                                        <SafeLink
                                          href={briefItem.urlExternal}
                                          style={{"word-break": "break-all", display: "block"}}
                                        >{briefItem.urlExternal}</SafeLink>
                                      )
                                    }
                                  </p>
                                </>
                              );
                            })}
                          </td>
                        </tr>
                      </>
                    );
                  })}
                </tbody>
              </TableStriped>
            </TableResponsive>
          </ContentSection>

          <ContentSection>
            <h2>Details of Grievance Redressal Mechanism (DP)</h2>
            <h2>The process of investor grievance redressal</h2>
            <TableResponsive>
              <TableStriped>
                <thead>
                  <tr>
                    <th>
                      S.No.
                    </th>
                    <th>
                      Type of Activity /Service
                    </th>
                    <th>
                      Brief about the Activity / Service
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {GrievanceProcessContent.map((GrievanceProcessItem, index) => {
                    return (
                      <>
                        <tr>
                          <td>
                            {index + 1}
                          </td>
                          <td>
                            {GrievanceProcessItem.activity}
                          </td>
                          <td>
                            {GrievanceProcessItem.brief.map((briefItem) => {
                              return (
                                <>
                                  <p>
                                    {briefItem.detail}
                                    {briefItem.url &&
                                      (
                                        <SafeLink href={briefItem.url}>Link</SafeLink>
                                      )
                                    }
                                    {briefItem.urlExternal &&
                                      (
                                        <SafeLink
                                          href={briefItem.urlExternal}
                                          style={{"word-break": "break-all", display: "block"}}
                                        >{briefItem.urlExternal}</SafeLink>
                                      )
                                    }
                                  </p>
                                </>
                              );
                            })}
                          </td>
                        </tr>
                      </>
                    );
                  })}
                </tbody>
              </TableStriped>
            </TableResponsive>
            <p style={{"padding-top": "20px"}}>
              For the Multi-level complaint resolution mechanism available at the Depositories, please refer to <SafeLink href="#ComplainResolutionProcess">Link</SafeLink>
            </p>
          </ContentSection>

          <ContentSection>
            <h2>Guidance pertaining to special circumstances related to Market activities: Termination of the Depository Participant</h2>
            <TableResponsive>
              <TableStriped>
                <thead>
                  <tr>
                    <th>
                      S.No.
                    </th>
                    <th>
                      Type of Activity /Service
                    </th>
                    <th>
                      Timelines for the Activity/ Service
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>1</td>
                    <td>
                      <p>
                        Depositories also provide value added services such as:
                      </p>
                      <p>
                        * Depositories to terminate the participation in case a participant no longer meets the eligibility criteria and/or any other grounds as mentioned in the bye laws like suspension of trading member by the Stock Exchanges.
                      </p>
                      <p>
                        * Participant surrenders the participation by its own wish.
                      </p>
                    </td>
                    <td>
                      <p>
                        Client will have a right to transfer all its securities to any other Participant of its choice without any charges for the transfer within 30 days from the date of intimation by way of letter/email.
                      </p>
                    </td>
                  </tr>
                </tbody>
              </TableStriped>
            </TableResponsive>
          </ContentSection>

          <ContentSection>
            <h2>Dos and Don’ts for Investors</h2>
            <p>For Dos and Don’ts please refer to the <SafeLink href="#DoDontInvestor">Link</SafeLink></p>
          </ContentSection>

          <ContentSection>
            <h2>Rights of Investors</h2>
            <p>For rights, please refer to the <SafeLink href="#RightsOfInvestor">Link</SafeLink></p>
          </ContentSection>

          <ContentSection>
            <h2>Responsibilities of Investors</h2>
            <p>For responsibilities, please refer to the <SafeLink href="#ResponsibilitesOfInvestor">Link</SafeLink></p>
          </ContentSection>

          <ContentSection>
            <h2>Annexure B – Information Contained in Links to the Investor Charter For Depositories and DPs</h2>
            <p>This document contains the contents pertaining to the qualifier “https://www.cdslindia.com/Investors/InvestorCharter.html” in the Investor Charter main document. The same is to be made available by the Depositories on their websites and web-links to the same is to be provided for incorporation in the Investor Charter.</p>
          </ContentSection>

          <ContentSection>
            <h2>Point No: 4 (2) of Investor Charter</h2>
            <h2 id="ValueAddedServices">Sub Point 1: Value Added Services</h2>
            <p>
              <span>Basic Services Demat Account (BSDA):</span> The facility of BSDA with limited services for eligible individuals was introduced with the objective of achieving wider financial inclusion and to encourage holding of demat accounts. No Annual Maintenance Charges (AMC) shall be levied, if the value of securities holding is upto Rs 4,00,000. For value of holdings more than Rs. 4,00,000 but upto Rs. 10,00,000, AMC not exceeding Rs 100 is chargeable.
            </p>
            <p>
              <span>TPIN Facility:</span> Due to a change in regulation by CDSL, if you are a non-POA customer, you’ll now have to use the CDSL TPIN and OTP instead of your PIN and authorise the debit of securities from your demat account against a delivery sale transaction.
            </p>
            <p>
              You would have received the CDSL TPIN to your registered mobile and email address (from edis@cdslindia.com) with CDSL.
            </p>
            <p>
              <span>Transposition cum dematerialization:</span> In case of transposition-cum dematerialisation, client can get securities dematerialised in the same account if the names appearing on the certificates match with the names in which the account has been opened but are in a different order. The same may be done by submitting the security certificates along with the Transposition Form and Demat Request Form.
            </p>
            <p>
              <span>Linkages with Clearing System:</span> For actual delivery of securities to the clearing system from the selling brokers and delivery of securities from the clearing system to the buying broker.
              <SafeLink href="https://www.cdslindia.com/Investors/Investorcharter.html" target="_blank" rel="noopener noreferrer">
                https://www.cdslindia.com/Investors/Investorcharter.html
              </SafeLink>
            </p>
          </ContentSection>

          <ContentSection>
            <h2>Point No: 4 (2) of Investor Charter</h2>
            <h2 id="Digitization">Sub Point 3: Digitization of services provided by the depositories</h2>
            <p>
              <span>E-account opening:</span> Account opening through digital mode, popularly known as “On-line Account opening”, wherein investor intending to open the demat account can visit DP website, fill in the required information, submit the required documents, conduct video IPV and demat account gets opened without visiting DPs office.
            </p>
            <p>
              <span>Online instructions for execution:</span> internet-enabled services like Speed-e (NSDL) & Easiest (CDSL) empower a demat account holder in managing his/her securities 'anytime-anywhere' in an efficient and convenient manner and submit instructions online without the need to use paper. These facilities allow Beneficial Owner (BO) to submit transfer instructions and pledge instructions including margin pledge from their demat account. The instruction facilities are also available on mobile applications through android, windows and IOS platforms.
            </p>
            <p>
              <span>e-DIS / Demat Gateway:</span> Investors can give instructions for transfer of securities through e-DIS apart from physical DIS. Here, for on-market transfer of securities, investors need to provide settlement number along with the ISIN and quantity of securities being authorized for transfer. Client shall be required to authorize each e-DIS valid for a single settlement number / settlement date, by way of OTP and PIN/password, both generated at Depositories end. Necessary risk containment measures are being adopted by Depositories in this regard.
            </p>
            <p>
              <span>e-CAS facility:</span> Consolidated Account Statements are available online and could also be accessed through mobile app to facilitate the investors to view their holdings in demat form.
            </p>
            <p>
              <span>Miscellaneous services:</span> Transaction alerts through SMS, e-locker facilities, chatbots for instantaneously responding to investor queries etc. have also been developed.
            </p>
          </ContentSection>


          <ContentSection>
            <h2>Point No: 5 (1) of Investor Charter</h2>
            <h2 id="InvestorCharter">Sub Point 2: Online Dispute Resolution (ODR) platform for online Conciliation and Arbitration</h2>
            <p>
              If the Investor is not satisfied with the resolution provided by DP or other Market Participants, then the Investor has the option to file the complaint/grievance on SMARTODR platform for its resolution through by online conciliation or arbitration.
              <br /> <SafeLink href="https://smartodr.in/login" target="_blank" rel="noopener noreferrer">
                https://smartodr.in/login
              </SafeLink>
            </p>
          </ContentSection>

          <ContentSection>
            <h2>Sub Point 3: Arbitration Proceedings</h2>

            {ArbitrationProceedings.map((ArbitrationProceedings) => {
              return (
                <>
                  <ul>
                    <li>
                      {ArbitrationProceedings}
                    </li>
                  </ul>
                </>
              );
            })}

          </ContentSection>

          <ContentSection>
            <h2>Point No: 5 (2) of Investor Charter</h2>
            <p id="ComplainResolutionProcess">
              Complaint Resolution process at Depositories
            </p>
            <img
              alt="Complaint Process"
              src="https://i.wlycdn.com/wealthy-home-page/wealthy-investor-charter.jpeg"
            />
          </ContentSection>

          <ContentSection>
            <h2 id="DoDontInvestor">Point No: 7 of Investor Charter</h2>
            <p>
              DOs and Don’ts for Investor
            </p>
            <TableResponsive>
              <TableStriped>
                <thead>
                  <tr>
                    <th>
                      S.No.
                    </th>
                    <th>
                      Guidance
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {InvestorGuidance.map((InvestorGuidanceItem, index) => {
                    return (
                      <>
                        <tr>
                          <td>
                            {index + 1}
                          </td>
                          <td>
                            {InvestorGuidanceItem}
                          </td>
                        </tr>
                      </>
                    );
                  })}
                  <tr>
                    <td>
                      24
                    </td>
                    <td>
                      If you have any grievance in respect of your demat account, please write to designated email IDs of depositories or you may lodge the same with SEBI online at <SafeLink href="https://scores.sebi.gov.in/" rel="noopener noreferrer" target="_blank">https://scores.sebi.gov.in/</SafeLink>
                    </td>
                  </tr>
                </tbody>
              </TableStriped>
            </TableResponsive>
          </ContentSection>

          <ContentSection>
            <h2 id="RightsOfInvestor">Point No: 8 of Investor Charter</h2>
            <p>
              Rights of Investors {" "}
              <SafeLink href="https://www.cdslindia.com/Investors/Investorcharter.html" target="_blank" rel="noopener noreferrer">
                https://www.cdslindia.com/Investors/Investorcharter.html
              </SafeLink>
            </p>
            <TableResponsive>
              <TableStriped>
                <tbody>
                  {InvestorRights.map((InvestorRightsItem, index) => {
                    return (
                      <>
                        <tr>
                          <td>
                            {index + 1}
                          </td>
                          <td>
                            {InvestorRightsItem}
                          </td>
                        </tr>
                      </>
                    );
                  })}
                </tbody>
              </TableStriped>
            </TableResponsive>
          </ContentSection>

          <ContentSection>
            <h2 id="ResponsibilitesOfInvestor">Point No: 9 of Investor Charter</h2>
            <p>
              <b>Responsibilities of Investors <br /></b>
              <SafeLink href="https://www.cdslindia.com/Investors/Investorcharter.html" target="_blank" rel="noopener noreferrer">
                https://www.cdslindia.com/Investors/Investorcharter.html
              </SafeLink>
            </p>
            <TableResponsive>
              <TableStriped>
                <tbody>
                  {InvestorResponsibilites.map((InvestorResponsibilitesItem, index) => {
                    return (
                      <>
                        <tr>
                          <td>
                            {index + 1}
                          </td>
                          <td>
                            {InvestorResponsibilitesItem}
                          </td>
                        </tr>
                      </>
                    );
                  })}
                </tbody>
              </TableStriped>
            </TableResponsive>
          </ContentSection>

          <ContentSection>
            <h2 id="ResponsibilitesOfInvestor">Code of Conduct for Depositories</h2>
            <SafeLink href="https://www.cdslindia.com/Investors/Investorcharter.html" target="_blank" rel="noopener noreferrer">
              https://www.cdslindia.com/Investors/Investorcharter.html
            </SafeLink>
            <p>
              <b>(Part D of Third Schedule of SEBI (D & P) regulations, 2018) <br /></b>
            </p>
            <p>
              A Depository shall:
            </p>
            <TableResponsive>
              <TableStriped>
                <tbody>
                  {ConductforDepositor.map((ConductforDepositorItem, index) => {
                    return (
                      <>
                        <tr>
                          <td>
                            {index + 1}
                          </td>
                          <td>
                            {ConductforDepositorItem}
                          </td>
                        </tr>
                      </>
                    );
                  })}
                </tbody>
              </TableStriped>
            </TableResponsive>
          </ContentSection>

          <ContentSection>
            <h2 id="ResponsibilitesOfInvestor">Code of Conduct for Participants</h2>
            <SafeLink href="https://www.cdslindia.com/Investors/Investorcharter.html" target="_blank" rel="noopener noreferrer">
              https://www.cdslindia.com/Investors/Investorcharter.html
            </SafeLink>
            <p>
              <b>(Part A of Third Schedule of SEBI (D & P) regulations, 2018) <br /></b>
            </p>
            <TableResponsive>
              <TableStriped>
                <tbody>
                  {ConductforParticipants.map((ConductforParticipantsItem, index) => {
                    return (
                      <>
                        <tr>
                          <td>
                            {index + 1}
                          </td>
                          <td>
                            {ConductforParticipantsItem}
                          </td>
                        </tr>
                      </>
                    );
                  })}
                </tbody>
              </TableStriped>
            </TableResponsive>
          </ContentSection>

          <ContentSection>
            <h2>Investor Complaint Redressal Mechanism</h2>
            <h2>Objective</h2>
            <p>
              We believe that Investor service is a vital element for sustained business growth and we want to ensure that our Investors receive exemplary service across different touch points. Prompt and efficient service is essential for retaining existing relationships and therefore Investor satisfaction becomes critical to us. This policy details grievance handling through a structured grievance redressal framework.
            </p>
          </ContentSection>

          <ContentSection>
            <h2>Policy</h2>
            <p>
              Where the Client has got any grievances, at branch level or HO level, he/she should first promptly notify the same to the Compliance Officer of our company in writing so as to reach our registered office or through email to our Investor Grievance Cell stating the nature and value (if any) of the grievance along with the relevant supporting documents. All the Grievances are time bound and the clients should strictly adhere to such limitation of time.
            </p>
          </ContentSection>

          <ContentSection>
            <h2>Compliance Officer: Mr. ANOOP P R</h2>
            <p>
              <span>Email ID: <SafeLink href="mailto:compliance@wealthy.in">compliance@wealthy.in</SafeLink></span>
            </p>
            <TableResponsive>
              <TableStriped>
                <tbody>
                  <tr>
                    <td>1</td>
                    <td>
                      The company has a designated investor grievances email id <SafeLink href="mailto:compliance@wealthy.in">compliance@wealthy.in</SafeLink> on which the client or investor can make a complaint.
                    </td>
                  </tr>
                  {ComplianceContent.map((ComplianceContentItem, index) => {
                    return (
                      <>
                        <tr>
                          <td>
                            {index + 2}
                          </td>
                          <td>
                            {ComplianceContentItem}
                          </td>
                        </tr>
                      </>
                    );
                  })}
                </tbody>
              </TableStriped>
            </TableResponsive>
            <p style={{"padding-top": "20px"}}>
              If not satisfied with the resolution of the Grievances, the client may approach the concerned Stock Exchange (s) at the following
            </p>
            <TableResponsive>
              <TableStriped>
                <thead>
                  <tr>
                    <th>
                      Exchange
                    </th>
                    <th>
                      Website
                    </th>
                    <th>
                      Contact No
                    </th>
                    <th>
                      Email ID
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {StockExchanges.map((StockExchangesItem, index) => {
                    return (
                      <>
                        <tr>
                          <td>
                            {StockExchangesItem.exchange}
                          </td>
                          <td>
                            <SafeLink
                              href={StockExchangesItem.actualUrl}
                              rel="noopener noreferrer"
                              target="_blank"
                            >
                              {StockExchangesItem.urlText}
                            </SafeLink>
                          </td>
                          <td>
                            {StockExchangesItem.tel}
                          </td>
                          <td>
                            {StockExchangesItem.email}
                          </td>
                        </tr>
                      </>
                    );
                  })}
                </tbody>
              </TableStriped>
            </TableResponsive>
            <p style={{"padding-top": "20px"}}>
              After approaching us and the Exchanges, if client is not satisfied, he/she can lodge the grievances with SEBI at <SafeLink href="https://scores.sebi.gov.in/">https://scores.sebi.gov.in/</SafeLink>.
            </p>
            <p>
              Toll Free Helpline at 1800 22 7575 / 1800 266 7575.
            </p>
          </ContentSection>

          <ContentSection>
            <h2>Information related to RA (Research Analyst)</h2>
            <ol>
              <li>
                <SafeLink
                  href="https://drive.google.com/file/d/1lm-M1KYG-dn-QvGqVMl2PWpf3ElUjAoV/view?usp=drive_link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                      Investor Charter for RA
                </SafeLink>
              </li>
              <li>
                <SafeLink
                  href="https://drive.google.com/file/d/1OqF3ez_Hopy_hJGuFJ2eFveCky_n14IH/view?usp=drive_link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                      MITC for RA
                </SafeLink>
              </li>
              <li>
                <SafeLink
                  href="https://drive.google.com/file/d/1arSjYozx0CloCpp9O6BspgXkFMgPncFD/view?usp=drive_link "
                  target="_blank"
                  rel="noopener noreferrer"
                >
                      Terms and Conditions
                </SafeLink>
              </li>
              <li>
                <SafeLink
                  href="https://drive.google.com/file/d/1MUrHD0qdOCjhnzWWT_rZzq7BTx1hNXen/view?usp=drive_link "
                  target="_blank"
                  rel="noopener noreferrer"
                >
                      Disclaimer & Disclosures
                </SafeLink>
              </li>
              <li>
                <SafeLink
                  href="https://drive.google.com/file/d/1mfXm2sneR4rzODoyVfGLkBfUOXkNe0Xq/view?usp=drive_link "
                  target="_blank"
                  rel="noopener noreferrer"
                >
                      Escalation Matrix for Research Analyst
                </SafeLink>
              </li>
              <li>
                <SafeLink
                  href="https://drive.google.com/file/d/1dPC-wiYqrUy1Qm8tabyI_TROTqzOxAMj/view?usp=drive_link "
                  target="_blank"
                  rel="noopener noreferrer"
                >
                      Investor Complaint Redressal Mechanism against RA
                </SafeLink>
              </li>
            </ol>
          </ContentSection>

          <ContentSection>
            <h2>Investor Grievance Reports</h2>
            <DownloadListContainer>
              {
                downloadItems.map((eachItem) => {
                  return (
                    <SafeLink
                      download
                      className="download-link"
                      href={eachItem.link}
                      key={eachItem.id}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <span className="download-text">{eachItem.name}</span>
                      <img
                        alt="download"
                        src={`${WealthyEnv.HOMEPAGE_CDN_URL}/download-icon-purple.png`}
                      />
                    </SafeLink>
                  );
                })
              }
            </DownloadListContainer>
          </ContentSection>

          <ContentSection>
            <h2>Policy Review</h2>
            <p>
              This policy may be reviewed as and when there are any changes introduced by any statutory authority or as and when it is found necessary to change the policy due to business needs.
            </p>
          </ContentSection>

        </StaticContent>
      </StaticWrapper>
      <NewFooter />
    </>
  );
};

const DownloadListContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;
  margin-top: 2rem;
  a {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.2rem 2.5rem;
    border-radius: 7px;
    border: 1px solid ${colors.primary};
    box-shadow: inset 0 0 10px rgb(103 37 244 / 20%);
    transition: all 0.2s ease-in-out;
    font-weight: 600;
    img {
      height: 22px;
      width: auto;
    }
    &:hover {
      background-color: ${colors.primary};
      color: #fff;
      img {
        filter: invert(1) brightness(100);
      }
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 576px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

export default InvestorCharter;
